import React, { useCallback, useEffect, useState } from "react";
import { playResult } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { motion } from "framer-motion";
import { Ear, Play, RotateCcw, Check, X, Flame, Trophy, Mic, Music2 } from "lucide-react";
import confetti from "canvas-confetti";
import { Navbar } from "@/components/Navbar";
import { StaffViewer } from "@/components/StaffViewer";
import { Button } from "@/components/ui/button";
import { initAudio, playPitch, playSequence } from "@/lib/audio";
import { EarLeaderboard } from "@/components/EarLeaderboard";

const WHITE = ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5"];
const LOW = ["G3", "A3", "B3"];
const SOLFA = { G3: "Sol,", A3: "La,", B3: "Ti,", C4: "Do", D4: "Re", E4: "Mi", F4: "Fa", G4: "Sol", A4: "La", B4: "Ti", C5: "Do'" };
const LEVELS = {
  easy: { label: "Easy", length: 3, pool: WHITE.slice(0, 5), maxLeap: 1 },
  medium: { label: "Medium", length: 4, pool: WHITE, maxLeap: 2 },
  hard: { label: "Hard", length: 5, pool: WHITE, maxLeap: 4 },
  songs: { label: "Real songs", songs: true },
};
const SONGS = [
  { title: "Amazing Grace", notes: ["G3", "C4", "E4", "C4", "E4", "D4", "C4", "A3"] },
  { title: "Joy to the World", notes: ["C5", "B4", "A4", "G4", "F4", "E4", "D4", "C4"] },
  { title: "Ode to Joy", notes: ["E4", "E4", "F4", "G4", "G4", "F4", "E4", "D4"] },
  { title: "When the Saints Go Marching In", notes: ["C4", "E4", "F4", "G4"] },
  { title: "Silent Night", notes: ["G4", "A4", "G4", "E4"] },
  { title: "Kumbaya", notes: ["C4", "E4", "G4", "G4", "G4", "A4", "A4", "G4"] },
  { title: "Michael, Row the Boat Ashore", notes: ["C4", "E4", "G4", "E4", "G4", "A4", "G4"] },
  { title: "Holy, Holy, Holy", notes: ["C4", "C4", "E4", "E4", "G4", "G4"] },
  { title: "The Doxology (Old Hundredth)", notes: ["C4", "C4", "B3", "A3", "G3", "C4", "D4", "E4"] },
  { title: "Auld Lang Syne", notes: ["G3", "C4", "C4", "C4", "E4", "D4", "C4", "D4"] },
  { title: "Twinkle, Twinkle, Little Star", notes: ["C4", "C4", "G4", "G4", "A4", "A4", "G4"] },
  { title: "Mary Had a Little Lamb", notes: ["E4", "D4", "C4", "D4", "E4", "E4", "E4"] },
  { title: "Swing Low, Sweet Chariot", notes: ["E4", "C4", "E4", "E4", "C4", "A3", "G3"] },
  { title: "Go Tell It on the Mountain", notes: ["E4", "E4", "D4", "C4", "A3", "G3"] },
  { title: "What a Friend We Have in Jesus", notes: ["G4", "G4", "A4", "G4", "E4", "C4"] },
  { title: "Jingle Bells", notes: ["E4", "E4", "E4", "E4", "E4", "E4", "E4", "G4"] },
];
const BEST_KEY = "jthub_melody_best";

const makeMelody = ({ length, pool, maxLeap, songs }, prev) => {
  if (songs) {
    const choices = SONGS.filter((x) => x.title !== prev);
    const song = choices[Math.floor(Math.random() * choices.length)];
    return { notes: song.notes, title: song.title };
  }
  const out = ["C4"];
  while (out.length < length) {
    const i = pool.indexOf(out[out.length - 1]);
    const opts = pool.filter((_, j) => j !== i && Math.abs(j - i) <= maxLeap);
    out.push(opts[Math.floor(Math.random() * opts.length)]);
  }
  return { notes: out, title: null };
};
const readBest = () => { try { return JSON.parse(localStorage.getItem(BEST_KEY)) || {}; } catch { return {}; } };

const Keyboard = ({ onKey, disabled, low }) => (
  <div className="mt-6 flex justify-center gap-1.5 overflow-x-auto" data-testid="melody-keyboard">
    {(low ? [...LOW, ...WHITE] : WHITE).map((p) => (
      <button key={p} disabled={disabled} onClick={() => onKey(p)} data-testid={`melody-key-${p}`}
        className="flex h-32 w-10 shrink-0 flex-col items-center justify-end rounded-b-xl border border-border bg-white pb-2 text-black shadow-md transition-transform active:translate-y-0.5 disabled:opacity-50 sm:w-16">
        <span className="text-sm font-semibold">{p[0]}</span>
        <span className="text-[10px] text-zinc-500">{SOLFA[p]}</span>
      </button>
    ))}
  </div>
);

export default function MelodyEarTrainer() {
  const [level, setLevel] = useState("easy");
  const [tune, setTune] = useState(() => makeMelody(LEVELS.easy));
  const melody = tune.notes;
  const isSongs = !!LEVELS[level].songs;
  const [answer, setAnswer] = useState([]);
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [score, setScore] = useState({ right: 0, total: 0 });
  const [best, setBest] = useState(readBest);
  const [sessionBest, setSessionBest] = useState(0);

  const listen = useCallback(() => { initAudio(); playSequence(melody, 0.75, 0.7); }, [melody]);
  const next = (lv = level) => {
    const t = makeMelody(LEVELS[lv], tune.title);
    setTune(t); setAnswer(LEVELS[lv].songs ? [t.notes[0]] : []); setResult(null);
  };
  useEffect(() => { next(level); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [level]);

  const tap = (p) => {
    initAudio(); playPitch(p, 0.7);
    const a = [...answer, p];
    setAnswer(a);
    if (a.length < melody.length) return;
    const ok = a.every((x, i) => x === melody[i]);
    setResult(ok);
    setTimeout(() => playResult(ok), 450);
    setScore((s) => ({ right: s.right + (ok ? 1 : 0), total: s.total + 1 }));
    const ns = ok ? streak + 1 : 0;
    setStreak(ns);
    setSessionBest((b) => Math.max(b, ns));
    if (ok && ns > (best[level] || 0)) {
      const b = { ...best, [level]: ns };
      setBest(b); localStorage.setItem(BEST_KEY, JSON.stringify(b));
      if (ns >= 3) confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 } });
    }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-12" data-testid="melody-ear-trainer">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><Ear size={14} /> Training</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Melody Ear Trainer</h1>
        <SoundToggle testId="melody-sound-toggle" className="mt-3" />
        <p className="mt-3 max-w-2xl text-muted-foreground">The melody is the heart of every song. Listen to a short tune, sing it back, then tap the notes you hear. Practice melodies start on Do (C); in Real songs you get the first note free — can you name the tune too?</p>

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="melody-levels">
          {Object.entries(LEVELS).map(([k, v]) => (
            <button key={k} onClick={() => { setLevel(k); setStreak(0); setSessionBest(0); setScore({ right: 0, total: 0 }); }} data-testid={`melody-level-${k}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${k === level ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/60"}`}>
              {v.songs ? "Real songs · hymns & classics" : `${v.label} · ${v.length} notes`}
            </button>
          ))}
          <div className="ml-auto flex gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1" data-testid="melody-streak"><Flame size={15} className="text-primary" /> {streak}</span>
            <span className="flex items-center gap-1" data-testid="melody-best"><Trophy size={15} className="text-primary" /> Best {best[level] || 0}</span>
            <span data-testid="melody-score">{score.right}/{score.total}</span>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-border bg-card p-6 text-center sm:p-10">
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={listen} className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90" data-testid="melody-play-btn"><Play size={16} className="mr-2" /> Play melody</Button>
            <Button variant="outline" onClick={() => { initAudio(); playPitch("C4", 1); }} className="rounded-full border-border" data-testid="melody-do-btn"><Music2 size={16} className="mr-2" /> Hear Do</Button>
          </div>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><Mic size={13} /> Sing it back out loud first, then tap the notes in order.</p>

          <div className="mt-6 flex justify-center gap-2" data-testid="melody-answer">
            {melody.map((m, i) => {
              const a = answer[i];
              const state = result === null ? (a ? "filled" : "empty") : a === m ? "right" : "wrong";
              return (
                <span key={i} className={`flex h-12 w-14 items-center justify-center rounded-xl border font-mono text-sm ${state === "right" ? "border-emerald-500 bg-emerald-500/15 text-emerald-500" : state === "wrong" ? "border-red-500 bg-red-500/15 text-red-500" : state === "filled" ? "border-primary bg-primary/10" : "border-dashed border-border"}`} data-testid={`melody-slot-${i}`}>
                  {a ? SOLFA[a] : "?"}
                </span>
              );
            })}
          </div>

          <Keyboard onKey={tap} disabled={result !== null} low={isSongs} />

          {result !== null && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="melody-result">
              <p className={`flex items-center justify-center gap-2 font-serif text-2xl ${result ? "text-emerald-500" : "text-red-500"}`}>
                {result ? <><Check /> Spot on!</> : <><X /> Not quite — here's the melody</>}
              </p>
              {tune.title && <p className="mt-2 text-muted-foreground" data-testid="melody-song-title">That was <span className="font-medium text-foreground">{tune.title}</span></p>}
              <div className="mt-4 flex justify-center">
                <StaffViewer clef="treble" notes={melody.map((p) => ({ pitch: p, label: SOLFA[p] }))} width={420} testId="melody-reveal-staff" />
              </div>
              <div className="mt-4 flex justify-center gap-3">
                <Button variant="outline" onClick={listen} className="rounded-full border-border" data-testid="melody-replay-btn"><Play size={15} className="mr-2" /> Hear it again</Button>
                <Button onClick={() => next()} className="rounded-full bg-primary text-primary-foreground" data-testid="melody-next-btn">Next melody</Button>
              </div>
            </motion.div>
          )}
          {result === null && answer.length > (isSongs ? 1 : 0) && (
            <button onClick={() => setAnswer(isSongs ? [melody[0]] : [])} className="mt-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="melody-clear-btn"><RotateCcw size={14} /> Clear and try again</button>
          )}
        </div>
        <EarLeaderboard level={level} levelLabel={LEVELS[level].label} sessionBest={sessionBest} correct={score.right} />
      </div>
    </div>
  );
}
